import { useEffect, useRef } from 'react';
import API from '../utils/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function SocialLogin({ onSuccess, onError }) {
  const googleRef = useRef(null);
  const appleRef  = useRef(null);

  // ── Google One Tap + button ──────────────────────────────────────
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const scriptId = 'gsi-client';
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id  = scriptId;
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = initGoogle;
      document.head.appendChild(s);
    } else if (window.google?.accounts) {
      initGoogle();
    }

    function initGoogle() {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
      });
      if (googleRef.current) {
        window.google.accounts.id.renderButton(googleRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: googleRef.current.offsetWidth || 320,
        });
      }
    }
  }, []);

  async function handleGoogleCredential(response) {
    try {
      const { data } = await API.post('/auth/google/token', { credential: response.credential });
      onSuccess?.(data);
    } catch (err) {
      onError?.(err.response?.data?.error || 'Google sign-in failed');
    }
  }

  // ── Apple Sign In ────────────────────────────────────────────────
  useEffect(() => {
    const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
    if (!APPLE_CLIENT_ID) return;

    const scriptId = 'apple-signin';
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id  = scriptId;
      s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      s.async = true;
      s.onload = () => {
        window.AppleID.auth.init({
          clientId:    APPLE_CLIENT_ID,
          scope:       'name email',
          redirectURI: window.location.origin + '/login',
          usePopup:    true,
        });
      };
      document.head.appendChild(s);
    }

    async function handleAppleSuccess(event) {
      try {
        const { authorization, user } = event.detail;
        const { data } = await API.post('/auth/apple/token', { id_token: authorization.id_token, user });
        onSuccess?.(data);
      } catch (err) {
        onError?.(err.response?.data?.error || 'Apple sign-in failed');
      }
    }
    document.addEventListener('AppleIDSignInOnSuccess', handleAppleSuccess);
    return () => document.removeEventListener('AppleIDSignInOnSuccess', handleAppleSuccess);
  }, []);

  const hasGoogle = !!GOOGLE_CLIENT_ID;
  const hasApple  = !!process.env.REACT_APP_APPLE_CLIENT_ID;

  if (!hasGoogle && !hasApple) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, margin:'20px 0' }}>
        <div style={{ flex:1, height:1, background:'var(--border)' }} />
        <span style={{ fontSize:12, color:'var(--text-soft)', whiteSpace:'nowrap' }}>or continue with</span>
        <div style={{ flex:1, height:1, background:'var(--border)' }} />
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {hasGoogle && (
          <div ref={googleRef} style={{ width:'100%', minHeight:44 }} />
        )}

        {hasApple && (
          <div
            id="appleid-signin"
            data-color="black"
            data-border="true"
            data-type="sign in"
            style={{ width:'100%', height:44, cursor:'pointer' }}
          />
        )}
      </div>
    </div>
  );
}
